import { type FormEvent, useEffect, useState } from "react";

function AndroidIcon() {
  return <svg className="android-icon" viewBox="0 0 120 120" role="img" aria-label="Android">
    <path d="M35 34 25 18M85 34l10-16" />
    <path d="M28 48c2-19 14-29 32-29s30 10 32 29" />
    <path className="android-fill" d="M27 48h66v43c0 8-6 14-14 14H41c-8 0-14-6-14-14V48Z" />
    <path d="M27 58H17v29M93 58h10v29M44 103v12m32-12v12" />
    <circle cx="45" cy="36" r="3.5" />
    <circle cx="75" cy="36" r="3.5" />
  </svg>;
}

export function Login() {
  const [username, setUsername] = useState("is4f");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string>();
  const expired = new URLSearchParams(window.location.search).has("expired");

  useEffect(() => {
    document.title = "Sign in · IS4F Android Emulator";
    fetch("/auth/session", { headers: { Accept: "application/json" } })
      .then((response) => {
        if (response.ok) window.location.replace("/");
      })
      .catch(() => { /* The form will report a connection error on submit. */ })
      .finally(() => setChecking(false));
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!username.trim() || !password) return;
    setBusy(true);
    setError(undefined);
    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!response.ok) {
        let message = response.status === 429
          ? "Too many sign-in attempts. Wait a minute and try again."
          : "Incorrect username or password.";
        try {
          const payload = await response.json();
          message = payload.detail?.message || message;
        } catch { /* Keep the status-specific message. */ }
        throw new Error(message);
      }
      window.location.replace("/");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to reach the login service.");
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return <main className="login-page">
    <section className="login-card" aria-labelledby="login-title">
      <div className="login-brand" aria-label="Android IS4F">
        <div className="login-android"><AndroidIcon /></div>
        <div className="login-is4f"><strong>IS4F</strong><span>Android Emulator</span></div>
      </div>
      <div className="login-form-panel">
        <span className="login-product">IS4F Android Emulator</span>
        <h1 id="login-title">Sign in</h1>
        <p>Access the private Android workspace and autonomous agent.</p>
        {expired && !error && <div className="login-message">Your session expired. Sign in again.</div>}
        {error && <div className="login-error" role="alert">{error}</div>}
        <form onSubmit={submit}>
          <label><span>Username</span><input
            name="username" autoComplete="username" autoCapitalize="none" spellCheck={false}
            value={username} onChange={(event) => setUsername(event.target.value)}
            disabled={busy || checking} required autoFocus
          /></label>
          <label><span>Password</span><input
            name="password" type="password" autoComplete="current-password"
            value={password} onChange={(event) => setPassword(event.target.value)}
            disabled={busy || checking} required
          /></label>
          <button type="submit" disabled={busy || checking || !username.trim() || !password}>
            {checking ? "Checking session…" : busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <small>Protected session · delivered securely through Caddy</small>
      </div>
    </section>
  </main>;
}
